import React from "react";
import * as styles from "./CustomRichTextElement.module.css";
import { RichTextElement } from "@kontent-ai/gatsby-components";
import { KontentRichText } from "../../../types/index";
import { Cta } from "../Cta/Cta";
import { formatLinks } from "../../../lib/links";
import { DomElement, domToReact } from "html-react-parser";
import FormattedRichTextContainer from "../../blocks/FormattedRichText/index";
import { RichTextImage } from "../RichTextImage/RichTextImage";
import LinkedImageContainer from "../../blocks/LinkedImage";
import EmbedContainer from "../../blocks/Embed";
import LoginFormContainer from "../../blocks/LoginForm";
import { Link } from "../Link/Link";

type Link = {
  codename: string;
  url_slug: string;
  url: string;
  type: string;
  link_id: string;
};

interface KontentItemComponent {
  internal: {
    type: string;
  };
  elements: any;
  system?: {
    id: string;
    codename: string;
  };
}

interface Props {
  content: KontentRichText;
  style?: React.CSSProperties;
  classes?: string;
  anchor?: string;
}

export const CustomRichTextElement: React.FC<Props> = ({
  content,
  style,
  classes,
  anchor,
}) => {
  if (!content) {
    return null;
  }

  return (
    <div
      id={anchor}
      className={`${styles.richtextelement} ${classes}`}
      style={style}
    >
      <RichTextElement
        value={content?.value}
        images={content?.images}
        links={content?.links}
        resolveLink={(link: Link, domNode: DomElement) => {
          let path = link?.url;

          if (domNode?.children && path) {
            return <a href={path}>{domToReact(domNode.children)}</a>;
          }
        }}
        linkedItems={content?.modular_content}
        resolveDomNode={(domNode: DomElement) => {
          if (domNode.name === "a") {
            return (
              <Link to={domNode.attribs?.href} {...domNode.attribs}>
                {domToReact(domNode.children)}
              </Link>
            );
          }

          return domToReact(domNode);
        }}
        resolveLinkedItem={(linkedItem: KontentItemComponent) => {
          // Debug
          // return <pre>{JSON.stringify(linkedItem, undefined, 2)}</pre>

          switch (linkedItem?.internal?.type) {
            case "kontent_item_link":
              let cta = formatLinks([linkedItem])[0];

              return <Cta {...cta} />;

            case "kontent_item_formatted_rich_text":
              return (
                <FormattedRichTextContainer
                  name={linkedItem?.internal?.type}
                  key={linkedItem?.system?.id}
                  id={linkedItem?.system?.id}
                  {...linkedItem?.elements}
                />
              );

            case "kontent_item_linked_image":
              return (
                <LinkedImageContainer
                  name={linkedItem?.internal?.type}
                  key={linkedItem?.system?.id}
                  id={linkedItem?.system?.id}
                  {...linkedItem?.elements}
                />
              );

            case "kontent_item_embed":
              return (
                <EmbedContainer
                  name={linkedItem?.internal?.type}
                  key={linkedItem?.system?.id}
                  id={linkedItem?.system?.id}
                  {...linkedItem?.elements}
                />
              );

            case "kontent_item_login_form":
              return (
                <LoginFormContainer
                  name={linkedItem?.internal?.type}
                  key={linkedItem?.system?.id}
                  id={linkedItem?.system?.id}
                  {...linkedItem?.elements}
                />
              );

            case "kontent_item_image":
              return <RichTextImage {...linkedItem} />;

            default:
              return (
                <p>
                  <strong>{linkedItem?.internal?.type}</strong> component not
                  available in rich text element. To correct, add component to{" "}
                  <code>CustomRichTextElement</code> in
                  <code>src/components/elements/CustomRichTextElement</code>.
                </p>
              );
          }
        }}
      />
    </div>
  );
};
